import type { TenantAnalyticsAdapter, TenantAnalyticsEnvelope } from '../types';
import {
  type SnapPixelPublicConfig,
  parseSnapPixelPublicConfig,
} from './config';

/**
 * Snap Pixel standard-event parameters. PII-free by platform decision — the
 * user_email / user_phone_number fields of Snap's templates are intentionally
 * absent from this contract.
 */
export interface SnapPixelEventParameters {
  readonly item_ids?: readonly string[];
  readonly item_category?: string;
  readonly number_items?: number;
  readonly price?: number;
  readonly currency?: string;
  readonly transaction_id?: string;
  /** Snap's browser/CAPI dedup key — always the canonical eventId. */
  readonly client_dedup_id?: string;
}

/**
 * ADD_CART (not START_CHECKOUT) is deliberate: it is the event the client's
 * agency configured their campaigns against. Semantically it fires at our
 * begin_checkout moment — the customer picked a bookable item and entered
 * checkout, the closest thing a booking platform has to a cart.
 */
export type SnapPixelEventName =
  | 'PAGE_VIEW'
  | 'VIEW_CONTENT'
  | 'ADD_CART'
  | 'PURCHASE';

export type SnapPixelEventSink = (
  eventName: SnapPixelEventName,
  parameters: SnapPixelEventParameters,
) => void | Promise<void>;

export interface CreateSnapPixelAdapterOptions {
  readonly publicConfig: unknown;
  readonly send: SnapPixelEventSink;
  readonly id?: string;
  readonly enabled?: boolean;
}

export function createSnapPixelAdapter(
  options: CreateSnapPixelAdapterOptions,
): TenantAnalyticsAdapter {
  const publicConfig = requirePublicConfig(options.publicConfig);

  if (typeof options.send !== 'function') {
    throw new Error('Snap Pixel requires an event sink.');
  }

  const send = options.send;

  return Object.freeze({
    id: options.id ?? `snapchat:${publicConfig.pixelId}`,
    provider: 'snapchat',
    requiredConsent: Object.freeze(['advertising'] as const),
    enabled: options.enabled !== false,
    deliver: async (envelope: TenantAnalyticsEnvelope) => {
      if (!envelope.event.browserDocumentLocationSafe) {
        throw new Error(
          'Snap Pixel delivery suppressed for an unsafe browser document location.',
        );
      }

      const event = envelope.event;

      switch (event.name) {
        case 'page_view':
          await send('PAGE_VIEW', Object.freeze({}));
          return;

        case 'view_content':
        case 'begin_checkout':
          await send(
            event.name === 'view_content' ? 'VIEW_CONTENT' : 'ADD_CART',
            Object.freeze({
              item_ids: Object.freeze([event.contentId]),
              item_category: event.contentType,
              ...(event.name === 'begin_checkout' ? { number_items: 1 } : {}),
              ...(event.value !== undefined
                ? { price: event.value, currency: event.currency }
                : {}),
            }),
          );
          return;

        case 'purchase':
          await send(
            'PURCHASE',
            Object.freeze({
              price: event.value,
              currency: event.currency,
              transaction_id: event.transactionId,
              item_ids: Object.freeze(event.items.map((item) => item.itemId)),
              number_items: event.items.length,
              client_dedup_id: event.eventId,
            }),
          );
          return;
      }
    },
  });
}

function requirePublicConfig(value: unknown): SnapPixelPublicConfig {
  const publicConfig = parseSnapPixelPublicConfig(value);

  if (!publicConfig) {
    throw new Error('Invalid Snap Pixel public configuration.');
  }

  return publicConfig;
}
